import * as XLSX from 'xlsx';

export const QUALIFICATION_MATRIX_SHEET_NAME = '资质矩阵';
export const MAX_QUALIFICATION_MATRIX_IMPORT_FILE_BYTES = 5 * 1024 * 1024;
export const MAX_QUALIFICATION_MATRIX_IMPORT_CELLS = 20_000;

export interface QualificationMatrixWorkbookEmployee {
  id: number;
  employee_code: string;
  employee_name: string;
}

export interface QualificationMatrixWorkbookQualification {
  id: number;
  qualification_name: string;
}

export interface QualificationMatrixWorkbookCell {
  employee_id: number;
  employee_code: string;
  employee_name: string;
  qualification_id: number;
  qualification_name: string;
  qualification_level: number | null;
  row: number;
  column: number;
}

export interface QualificationMatrixWorkbookPreview {
  cells: QualificationMatrixWorkbookCell[];
  employeeCount: number;
  qualificationCount: number;
}

interface WorkbookReferenceData {
  employees: QualificationMatrixWorkbookEmployee[];
  qualifications: QualificationMatrixWorkbookQualification[];
}

const normalizeText = (value: unknown): string => String(value ?? '').trim();
const normalizeCode = (value: unknown): string => normalizeText(value).toUpperCase();

const formatCellLocation = (row: number, column: number): string => `第 ${row} 行第 ${column} 列`;

const parseLevel = (value: unknown, row: number, column: number): number | null => {
  const normalized = normalizeText(value);
  if (!normalized || normalized === '-') return null;

  const level = Number(normalized);
  if (!Number.isInteger(level) || level < 1 || level > 5) {
    throw new Error(`${formatCellLocation(row, column)}的资质等级必须为 1-5，空白或“-”表示移除资质`);
  }
  return level;
};

const assertNoFormulas = (sheet: XLSX.WorkSheet): void => {
  Object.entries(sheet).forEach(([address, value]) => {
    if (address.startsWith('!')) return;
    const cell = value as XLSX.CellObject;
    if (!cell?.f) return;

    const decoded = XLSX.utils.decode_cell(address);
    throw new Error(`${formatCellLocation(decoded.r + 1, decoded.c + 1)}包含公式；导入文件只允许静态值`);
  });
};

export const parseQualificationMatrixWorkbook = (
  data: ArrayBuffer,
  reference: WorkbookReferenceData,
): QualificationMatrixWorkbookPreview => {
  let workbook: XLSX.WorkBook;
  try {
    workbook = XLSX.read(data, { type: 'array' });
  } catch {
    throw new Error('无法解析 Excel 文件，请确认文件未损坏');
  }

  const sheet = workbook.Sheets[QUALIFICATION_MATRIX_SHEET_NAME];
  if (!sheet) {
    throw new Error(`缺少“${QUALIFICATION_MATRIX_SHEET_NAME}”工作表，请使用本页面导出的 Excel 文件`);
  }
  assertNoFormulas(sheet);

  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    defval: '',
    raw: true,
    blankrows: true,
  });
  const headerRowIndex = rows.findIndex((row) => (
    normalizeText(row[0]) === '资质ID' && normalizeText(row[1]) === '资质名称'
  ));
  if (headerRowIndex < 0 || !rows[headerRowIndex + 1]) {
    throw new Error('未找到资质矩阵表头，请使用本页面导出的 Excel 文件');
  }

  const employeeHeaderRow = rows[headerRowIndex];
  const employeeCodeRow = rows[headerRowIndex + 1];
  const employeeByCode = new Map(
    reference.employees.map((employee) => [normalizeCode(employee.employee_code), employee]),
  );
  const qualificationById = new Map(
    reference.qualifications.map((qualification) => [qualification.id, qualification]),
  );
  const workbookEmployeeCodes = new Set<string>();
  const scopedEmployees: Array<{ columnIndex: number; employee: QualificationMatrixWorkbookEmployee }> = [];

  for (let columnIndex = 2; columnIndex < employeeHeaderRow.length; columnIndex += 1) {
    const employeeName = normalizeText(employeeHeaderRow[columnIndex]);
    const employeeCode = normalizeCode(employeeCodeRow[columnIndex]);
    if (!employeeName && !employeeCode) continue;
    if (!employeeCode) {
      throw new Error(`${formatCellLocation(headerRowIndex + 2, columnIndex + 1)}缺少员工工号`);
    }
    if (workbookEmployeeCodes.has(employeeCode)) {
      throw new Error(`员工工号“${employeeCode}”在文件中重复`);
    }
    workbookEmployeeCodes.add(employeeCode);

    const employee = employeeByCode.get(employeeCode);
    if (!employee) {
      throw new Error(`员工工号“${employeeCode}”不存在，请重新导出最新矩阵`);
    }
    if (employeeName && employeeName !== employee.employee_name) {
      throw new Error(`员工工号“${employeeCode}”对应姓名已变化，请重新导出最新矩阵`);
    }
    scopedEmployees.push({ columnIndex, employee });
  }

  if (!scopedEmployees.length) {
    throw new Error('文件中没有可导入的员工列');
  }

  const cells: QualificationMatrixWorkbookCell[] = [];
  const workbookQualificationIds = new Set<number>();
  let qualificationCount = 0;

  for (let rowIndex = headerRowIndex + 2; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex];
    const rawQualificationId = normalizeText(row[0]);
    const workbookQualificationName = normalizeText(row[1]);
    if (!rawQualificationId && !workbookQualificationName) continue;

    const qualificationId = Number(rawQualificationId);
    if (!Number.isInteger(qualificationId) || qualificationId <= 0) {
      throw new Error(`${formatCellLocation(rowIndex + 1, 1)}的资质 ID 无效`);
    }
    if (workbookQualificationIds.has(qualificationId)) {
      throw new Error(`资质 ID“${qualificationId}”在文件中重复`);
    }
    workbookQualificationIds.add(qualificationId);

    const qualification = qualificationById.get(qualificationId);
    if (!qualification) {
      throw new Error(`资质 ID“${qualificationId}”不存在，请重新导出最新矩阵`);
    }
    if (workbookQualificationName !== qualification.qualification_name) {
      throw new Error(`资质 ID“${qualificationId}”对应名称已变化，请重新导出最新矩阵`);
    }

    qualificationCount += 1;
    scopedEmployees.forEach(({ columnIndex, employee }) => {
      cells.push({
        employee_id: employee.id,
        employee_code: employee.employee_code,
        employee_name: employee.employee_name,
        qualification_id: qualification.id,
        qualification_name: qualification.qualification_name,
        qualification_level: parseLevel(row[columnIndex], rowIndex + 1, columnIndex + 1),
        row: rowIndex + 1,
        column: columnIndex + 1,
      });
    });

    if (cells.length > MAX_QUALIFICATION_MATRIX_IMPORT_CELLS) {
      throw new Error(`单次最多导入 ${MAX_QUALIFICATION_MATRIX_IMPORT_CELLS} 个矩阵单元格`);
    }
  }

  if (!qualificationCount) {
    throw new Error('文件中没有可导入的资质行');
  }

  return {
    cells,
    employeeCount: scopedEmployees.length,
    qualificationCount,
  };
};

export const readQualificationMatrixWorkbook = async (
  file: File,
  reference: WorkbookReferenceData,
): Promise<QualificationMatrixWorkbookPreview> => {
  if (!file.name.toLowerCase().endsWith('.xlsx')) {
    throw new Error('只接受 .xlsx 文件，请使用本页面导出的 Excel 文件');
  }
  if (file.size > MAX_QUALIFICATION_MATRIX_IMPORT_FILE_BYTES) {
    throw new Error('文件不能超过 5 MB');
  }
  return parseQualificationMatrixWorkbook(await file.arrayBuffer(), reference);
};
